import { useContext, useEffect, useState, useCallback } from "react";
import { deasyWorkflowService } from "../../../services/api";
import { BaseContext } from "../../../contexts/BaseContext";
import { DeasyWorkflow } from "./DeasyWorkflow";
import {
  FiPlus,
  FiRefreshCw,
  FiActivity,
  FiLayers,
  FiInfo,
  FiClock,
  FiList,
  FiGrid,
} from "react-icons/fi";
import { WorkflowDialog } from "./WorkflowDialog";
import { DeletionDialog } from "./DeletionDialog";
import { InfoPanel } from "./utils";

export const DeasyWorkflows = () => {
  const { deasyApiKey } = useContext(BaseContext);

  // Workflow State
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(true);

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Cache fetchWorkflows with useCallback to prevent unnecessary recreations
  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const response =
        await deasyWorkflowService.listDeasyWorkflows(deasyApiKey);
      setWorkflows(response.data.workflows);

      // Show info panel for first-time users
      if (
        response.data.workflows.length === 0 &&
        !localStorage.getItem("workflowInfoSeen")
      ) {
        setShowInfoPanel(true);
      }
    } catch (error) {
      console.error("Failed to fetch workflows:", error);
    } finally {
      setLoading(false);
    }
  }, [deasyApiKey]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchWorkflows();
    } finally {
      setTimeout(() => {
        setRefreshing(false);
      }, 500); // Ensure animation is visible for at least 500ms
    }
  };

  // When a workflow is edited, ensure it's reflected in the UI
  const handleEditWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setOpenDialog(true);
  };

  const handleCreateWorkflow = () => {
    setSelectedWorkflow(null);
    setOpenDialog(true);
  };

  // Close dialog callback that refreshes workflows
  const handleCloseDialog = (shouldRefresh = false) => {
    setOpenDialog(false);
    if (shouldRefresh) {
      fetchWorkflows();
    }
  };

  const handleCloseInfoPanel = () => {
    setShowInfoPanel(false);
    localStorage.setItem("workflowInfoSeen", "true");
  };

  const handleToggleInfoPanel = () => {
    setShowInfoPanel(!showInfoPanel);
  };

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  return (
    <div className="p-6 font-sans relative">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50 pointer-events-none"></div>

      <div className="relative z-10">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <h2 className="text-2xl font-bold text-gray-800">Workflows</h2>
            <button
              className="ml-2 p-1.5 rounded-full text-gray-400 hover:text-primary hover:bg-primary/10 transition-all"
              onClick={handleToggleInfoPanel}
              title="Workflow information"
            >
              <FiInfo className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              className={`p-2.5 rounded-lg text-secondary hover:text-primary hover:bg-primary/10 transition-all ${
                refreshing ? "animate-spin text-primary" : ""
              }`}
              onClick={handleRefresh}
              title="Refresh workflows"
              disabled={loading}
            >
              <FiRefreshCw className="w-5 h-5" />
            </button>
            <button
              className="flex items-center px-5 py-2.5 bg-primary text-white rounded-lg shadow-md hover:bg-primary-dark hover:shadow-lg transition-all duration-300 group"
              onClick={handleCreateWorkflow}
            >
              <FiPlus className="mr-2 group-hover:scale-110 transition-transform" />{" "}
              Create Workflow
            </button>
          </div>
        </div>

        {/* Info Panel */}
        {showInfoPanel && <InfoPanel onClose={handleCloseInfoPanel} />}

        {loading ? (
          <div className="flex flex-col items-center justify-center my-16 gap-4">
            <div className="relative w-16 h-16">
              <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FiActivity className="w-6 h-6 text-primary animate-pulse" />
              </div>
            </div>
            <p className="text-gray-500 animate-pulse">Loading workflows...</p>
          </div>
        ) : workflows.length === 0 ? (
          <div className="flex flex-col items-center gap-6 p-12 bg-white border border-gray-200 rounded-xl shadow-sm text-center transition-all hover:border-primary/30 hover:shadow-md">
            <div className="p-5 bg-primary/10 rounded-full">
              <FiLayers size={40} className="text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                No workflows found
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Create your first workflow to automate document processing with
                sequential stages and parallel jobs.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-2xl mt-4">
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <div className="p-2 bg-primary/10 rounded-full mb-2">
                  <FiList className="w-5 h-5 text-primary" />
                </div>
                <h4 className="font-medium text-gray-800">Create Stages</h4>
                <p className="text-sm text-gray-500 text-center">
                  Define the sequential steps in your process
                </p>
              </div>
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <div className="p-2 bg-primary/10 rounded-full mb-2">
                  <FiGrid className="w-5 h-5 text-primary" />
                </div>
                <h4 className="font-medium text-gray-800">Add Parallel Jobs</h4>
                <p className="text-sm text-gray-500 text-center">
                  Run multiple tasks simultaneously within stages
                </p>
              </div>
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <div className="p-2 bg-primary/10 rounded-full mb-2">
                  <FiClock className="w-5 h-5 text-primary" />
                </div>
                <h4 className="font-medium text-gray-800">Set Frequency</h4>
                <p className="text-sm text-gray-500 text-center">
                  Control how often jobs execute
                </p>
              </div>
            </div>
            <button
              className="mt-6 flex items-center px-6 py-3 bg-primary text-white rounded-lg shadow-md hover:bg-primary-dark hover:shadow-lg transition-all duration-300 group"
              onClick={handleCreateWorkflow}
            >
              <FiPlus className="mr-2 group-hover:scale-110 transition-transform" />{" "}
              Create Your First Workflow
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {workflows.map((workflow) => (
              <DeasyWorkflow
                key={workflow.id}
                workflow={workflow}
                setSelectedWorkflow={setSelectedWorkflow}
                setOpenDialog={setOpenDialog}
                setOpenDeleteDialog={setOpenDeleteDialog}
                fetchWorkflows={fetchWorkflows}
                onEditWorkflow={handleEditWorkflow}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      {openDialog && (
        <WorkflowDialog
          setOpenDialog={handleCloseDialog}
          selectedWorkflow={selectedWorkflow}
          setWorkflows={setWorkflows}
          fetchWorkflows={fetchWorkflows}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {openDeleteDialog && (
        <DeletionDialog
          setOpenDeleteDialog={setOpenDeleteDialog}
          selectedWorkflow={selectedWorkflow}
          fetchWorkflows={fetchWorkflows}
        />
      )}
    </div>
  );
};
